import { Button, Input, Spinner, TextField } from "@heroui/react";
import { useState } from "react";
import SectionCard from "@/src/common/components/section-card";
import { env } from "@/src/common/constants/env";
import { useMcpTokens } from "@/src/settings/hooks/use-mcp-tokens";

const formatDate = (value: string): string =>
	new Date(value).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });

const McpTokensSection: React.FC = () => {
	const { tokens, isLoading, isCreating, hasError, error, created, create, revoke, dismissCreated } = useMcpTokens();
	const [name, setName] = useState("");
	const [copied, setCopied] = useState(false);

	const onCreate = (): void => {
		const trimmed = name.trim();
		if (trimmed.length === 0) {
			return;
		}
		setCopied(false);
		void create(trimmed).then(() => setName(""));
	};

	const onRevoke = (id: string): void => {
		if (window.confirm("Révoquer ce token ? Les clients MCP qui l'utilisent perdront l'accès.")) {
			void revoke(id);
		}
	};

	const onCopy = (secret: string): void => {
		void navigator.clipboard.writeText(secret).then(() => setCopied(true));
	};

	const configCommand = created
		? `claude mcp add storylines-studio --transport http ${env.SERVER_URL}/mcp --header "Authorization: Bearer ${created.secret}"`
		: "";

	return (
		<SectionCard
			title="Storylines Studio — tokens MCP"
			description="Tokens d'accès du serveur MCP (/mcp) pour connecter un assistant : Claude Code, Claude Desktop ou tout client MCP supportant un header Authorization."
			actions={
				<>
					<TextField value={name} onChange={setName} className="w-48">
						<Input placeholder="Nom (ex. claude-code)" />
					</TextField>
					<Button isDisabled={isCreating || name.trim().length === 0} onPress={onCreate}>
						{isCreating ? "Génération…" : "Générer un token"}
					</Button>
				</>
			}
		>
			{error ? <p className="text-danger text-sm">{error}</p> : null}

			{created ? (
				<div className="flex flex-col gap-3 rounded-xl border border-border-soft bg-glass p-4">
					<p className="text-sm">
						Token <span className="font-semibold">{created.token.name}</span> créé. Ce secret ne sera plus jamais affiché : copiez-le
						maintenant.
					</p>
					<code className="break-all rounded-lg bg-surface p-3 font-mono text-xs">{created.secret}</code>
					<p className="text-muted text-xs">Configuration Claude Code :</p>
					<code className="break-all rounded-lg bg-surface p-3 font-mono text-xs">{configCommand}</code>
					<div className="flex gap-2">
						<Button size="sm" onPress={() => onCopy(created.secret)}>
							{copied ? "Copié ✓" : "Copier le secret"}
						</Button>
						<Button size="sm" variant="secondary" onPress={dismissCreated}>
							J'ai copié le secret
						</Button>
					</div>
				</div>
			) : null}

			{isLoading ? (
				<div className="flex justify-center py-12">
					<Spinner size="lg" />
				</div>
			) : hasError ? (
				<p className="rounded-xl border border-border-soft bg-surface p-10 text-center text-danger text-sm">
					Impossible de charger les tokens MCP.
				</p>
			) : tokens.length === 0 ? (
				<p className="rounded-xl border border-border-soft border-dashed bg-glass p-10 text-center text-muted text-sm">
					Aucun token actif. Générez-en un pour connecter un assistant au Studio.
				</p>
			) : (
				<ul className="flex flex-col gap-2">
					{tokens.map((token) => (
						<li key={token.id} className="flex items-center justify-between gap-4 rounded-xl border border-border-soft bg-surface p-4">
							<div className="flex flex-col gap-1">
								<span className="font-medium text-sm">{token.name}</span>
								<span className="font-mono text-muted text-xs">{token.tokenPrefix}…</span>
							</div>
							<div className="flex items-center gap-6">
								<div className="flex flex-col gap-1 text-right text-muted text-xs">
									<span>Créé le {formatDate(token.createdAt)}</span>
									<span>
										{token.lastUsedAt ? `Utilisé le ${formatDate(token.lastUsedAt)}` : "Jamais utilisé"} · Expire le{" "}
										{formatDate(token.expiresAt)}
									</span>
								</div>
								<Button size="sm" variant="secondary" className="text-danger" onPress={() => onRevoke(token.id)}>
									Révoquer
								</Button>
							</div>
						</li>
					))}
				</ul>
			)}
		</SectionCard>
	);
};

export default McpTokensSection;
